import React from 'react';
import { Link } from 'react-router-dom';
import { BaseModal } from './BaseModal';

interface FooterInfoModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function FooterInfoModal({ isOpen, onClose }: FooterInfoModalProps) {
  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title="OneTrust Footer Integrations"
    >
      <div className="space-y-6 text-gray-600 dark:text-gray-300">
        <div className="bg-gray-50 dark:bg-gray-700 p-6 rounded-lg">
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
            Cookie Settings
          </h3>
          <p>
            The Cookie Settings button is directly integrated with OneTrust's Consent Management Platform. 
            When clicked, it opens the OneTrust preference center where you can:
          </p>
          <ul className="list-disc list-inside mt-2 space-y-1">
            <li>View all cookie categories</li>
            <li>Manage consent preferences</li>
            <li>Update your choices anytime</li>
            <li>Access detailed cookie information</li>
          </ul>
        </div>

        <div className="bg-gray-50 dark:bg-gray-700 p-6 rounded-lg">
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
            Privacy Policy
          </h3>
          <p>
            Our Privacy Policy page uses OneTrust's Privacy Notice Management solution. This integration:
          </p>
          <ul className="list-disc list-inside mt-2 space-y-1">
            <li>Dynamically loads the latest policy version</li>
            <li>Supports multiple languages</li>
            <li>Tracks policy updates</li>
            <li>Ensures compliance with regulations</li>
          </ul>
          <p className="mt-2">
            Visit the Privacy Policy page and click the OneTrust icon to learn more about this integration.
          </p>
        </div>

        <div className="bg-gray-50 dark:bg-gray-700 p-6 rounded-lg">
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
            Privacy Center
          </h3>
          <p>
            While not an OneTrust integration, our Privacy Center is an essential hub that:
          </p>
          <ul className="list-disc list-inside mt-2 space-y-1">
            <li>Centralizes privacy-related resources</li>
            <li>Provides easy access to privacy rights</li>
            <li>Offers transparency about data practices</li>
            <li>Complements OneTrust functionality</li>
          </ul>
        </div>

        <div className="bg-gray-50 dark:bg-gray-700 p-6 rounded-lg">
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
            Your Privacy Choices
          </h3>
          <p>
            Our preference center provides a comprehensive way to manage your privacy choices and communication preferences:
          </p>
          <ul className="list-disc list-inside mt-2 space-y-1">
            <li>Manage email and marketing preferences</li>
            <li>Control data sharing settings</li>
            <li>Update contact information</li>
            <li>View consent history</li>
          </ul>
          <p className="mt-4">
            Visit our{' '}
            <Link 
              to="/communication-preferences"
              onClick={onClose}
              className="text-[#5e9bb8] hover:text-[#499da8] transition-colors duration-200"
            >
              Communication Preferences
            </Link>
            {' '}page to explore different preference center implementations and find the one that best suits your needs.
          </p>
        </div>

        <div className="mt-6 bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
          <p className="text-sm text-blue-800 dark:text-blue-200">
            These integrations work together to provide a comprehensive privacy management system that 
            ensures compliance while offering a seamless user experience.
          </p>
        </div>
      </div>
    </BaseModal>
  );
}